import { useEffect, useMemo, useState } from 'react';

import * as api from '@/features/backlog/api';
import { useAppData } from '@/app-data';
import { Button } from '@/components/ui/Button';
import { SearchField } from '@/components/ui/SearchField';
import MemberAvatar from '@/components/member-avatar';
import { cn } from '@/components/ui/cn';

/**
 * Board-settings section (admins only) for who can access the board and who administers it. Default
 * is "all members" — everyone, including future ones. Switch to "only selected members" to restrict,
 * then grant access per member and flag board admins. Renders nothing for non-managers (the access
 * fetch 403s), so it silently hides for regular members.
 */
export default function BoardAccessSection({ boardId }: { boardId: string }) {
  const { members } = useAppData();
  const [mode, setMode] = useState<'all' | 'restricted'>('all');
  const [access, setAccess] = useState<Set<string>>(new Set());
  const [admins, setAdmins] = useState<Set<string>>(new Set());
  const [state, setState] = useState<'loading' | 'ready' | 'hidden'>('loading');
  const [saving, setSaving] = useState<'idle' | 'saving' | 'saved'>('idle');
  const [q, setQ] = useState('');

  useEffect(() => {
    let cancelled = false;
    setState('loading');
    api
      .getBoardAccess(boardId)
      .then((a) => {
        if (cancelled) return;
        setMode(a.access_mode === 'restricted' ? 'restricted' : 'all');
        setAccess(new Set(a.members.map((m) => m.member_id)));
        setAdmins(new Set(a.members.filter((m) => m.role === 'admin').map((m) => m.member_id)));
        setState('ready');
      })
      .catch(() => {
        if (!cancelled) setState('hidden');
      });
    return () => {
      cancelled = true;
    };
  }, [boardId]);

  const activeMembers = useMemo(
    () => [...members].filter((m) => m.active).sort((a, b) => a.name.localeCompare(b.name)),
    [members],
  );
  const shown = useMemo(() => {
    const s = q.trim().toLowerCase();
    if (!s) return activeMembers;
    return activeMembers.filter((m) => `${m.name} ${m.email ?? ''}`.toLowerCase().includes(s));
  }, [activeMembers, q]);

  const toggleAccess = (id: string) =>
    setAccess((prev) => {
      const n = new Set(prev);
      if (n.has(id)) {
        n.delete(id);
        setAdmins((a) => {
          const na = new Set(a);
          na.delete(id);
          return na;
        }); // losing access also drops admin
      } else n.add(id);
      return n;
    });
  const toggleAdmin = (id: string) =>
    setAdmins((prev) => {
      const n = new Set(prev);
      if (n.has(id)) n.delete(id);
      else {
        n.add(id);
        if (mode === 'restricted') setAccess((a) => new Set(a).add(id)); // an admin implicitly has access
      }
      return n;
    });

  const save = async () => {
    setSaving('saving');
    const ids = mode === 'all' ? [...admins] : [...new Set([...access, ...admins])];
    const payload = ids.map((id) => ({ board_id: boardId, member_id: id, role: (admins.has(id) ? 'admin' : 'member') as 'admin' | 'member' }));
    try {
      const res = await api.saveBoardAccess(boardId, { access_mode: mode, members: payload });
      setMode(res.access_mode === 'restricted' ? 'restricted' : 'all');
      setAccess(new Set(res.members.map((m) => m.member_id)));
      setAdmins(new Set(res.members.filter((m) => m.role === 'admin').map((m) => m.member_id)));
      setSaving('saved');
      setTimeout(() => setSaving('idle'), 1600);
    } catch {
      setSaving('idle');
    }
  };

  if (state !== 'ready') return null;

  const seg = (active: boolean) =>
    cn('flex-1 rounded-md px-3 py-1.5 text-[12.5px] font-semibold transition-colors', active ? 'bg-surface-raised text-fg-strong shadow-sm' : 'text-fg-muted hover:text-fg');

  return (
    <section className="mb-6 border-b border-line pb-6">
      <div className="mb-1 flex items-center gap-2">
        <div className="text-[13px] font-semibold text-fg-strong">Access &amp; admins</div>
        <div className="ml-auto flex items-center gap-2">
          {saving === 'saved' ? <span className="text-[11px] font-medium text-emerald-500">Saved</span> : null}
          <Button variant="primary" size="sm" onClick={save} disabled={saving === 'saving'}>
            {saving === 'saving' ? 'Saving…' : 'Save access'}
          </Button>
        </div>
      </div>
      <p className="mb-3 text-[12px] text-fg-faint">Who can open this board, and who can manage its settings.</p>

      <div className="mb-3 inline-flex w-full max-w-[320px] rounded-lg bg-layer p-0.5">
        <button type="button" className={seg(mode === 'all')} onClick={() => setMode('all')}>
          All members
        </button>
        <button type="button" className={seg(mode === 'restricted')} onClick={() => setMode('restricted')}>
          Only selected
        </button>
      </div>
      <p className="mb-3 text-[11.5px] text-fg-faint">
        {mode === 'all'
          ? 'Everyone — including members added later — can access this board. Flag anyone below as an admin.'
          : 'Only members you grant access to can open this board. Admins can also change these settings.'}
      </p>

      <SearchField value={q} onChange={setQ} placeholder="Search members…" />
      <div className="thin-scroll mt-2 max-h-[260px] overflow-auto rounded-xl border border-line">
        {shown.map((m) => {
          const hasAccess = mode === 'all' || access.has(m.id);
          const isAdmin = admins.has(m.id);
          return (
            <div key={m.id} className="flex items-center gap-2.5 border-b border-line px-3 py-2 last:border-b-0">
              <MemberAvatar member={m} size={26} />
              <div className="min-w-0 flex-1">
                <div className="truncate text-[13px] font-medium text-fg">{m.name}</div>
                <div className="truncate text-[11px] text-fg-faint">{m.email}</div>
              </div>
              {mode === 'restricted' ? (
                <label className="flex cursor-pointer items-center gap-1.5 text-[12px] text-fg-muted">
                  <input type="checkbox" checked={access.has(m.id)} onChange={() => toggleAccess(m.id)} className="accent-accent" />
                  Access
                </label>
              ) : (
                <span className="text-[11px] text-fg-ghost">Has access</span>
              )}
              <label className={cn('flex items-center gap-1.5 text-[12px]', hasAccess ? 'cursor-pointer text-fg-muted' : 'cursor-not-allowed text-fg-ghost')}>
                <input type="checkbox" checked={isAdmin} disabled={!hasAccess} onChange={() => toggleAdmin(m.id)} className="accent-accent" />
                Admin
              </label>
            </div>
          );
        })}
        {shown.length === 0 ? <div className="px-3 py-6 text-center text-[12px] text-fg-faint">No members match.</div> : null}
      </div>
    </section>
  );
}
